// =============================================================================
// PARTS4DRIVE GEOGRAPH — AFRICAN AUTOMOTIVE COMMERCE GRAPH
// Multi-tier hierarchy: Country -> Admin Level 1 (Region/State) -> Admin Level 2 (City) -> Commercial Hub
// =============================================================================

export interface GeoCoordinate {
  lat: number;
  lng: number;
}

export interface MarketHubNode {
  slug: string;
  name: string;
  countryCode: string;
  region: string;
  city: string;
  district: string;
  postalCode?: string;
  geo: GeoCoordinate;
  description: string;
  openingHours: string;
  popularCategories: string[];
}

export interface CityNode {
  name: string;
  slug: string;
  adminLevel1: string;
  hubs?: string[];
}

export interface RegionNode {
  name: string;
  slug: string;
  cities: CityNode[];
}

export interface CountryNode {
  code: string; // ISO 3166-1 alpha-2 uppercase
  slug: string; // ISO-2 lowercase for URL routing (/gh/, /ng/, etc.)
  name: string;
  flag: string; // Real country flag emoji
  demonym: string;
  region: string;
  currency: string;
  currencySymbol: string;
  language: string;
  vatRate: number;
  defaultReturnDays: number;
  dialCode: string;
  geo: GeoCoordinate;
  regions: RegionNode[];
  hubs: MarketHubNode[];
  popularMakes: string[];
}

export const AFRICAN_GEOGRAPH: Record<string, CountryNode> = {
  GH: {
    code: 'GH',
    slug: 'gh',
    name: 'Ghana',
    flag: '🇬🇭',
    demonym: 'Ghanaian',
    region: 'West Africa',
    currency: 'GHS',
    currencySymbol: 'GH₵',
    language: 'en',
    vatRate: 0.15,
    defaultReturnDays: 7,
    dialCode: '+233',
    geo: { lat: 5.6037, lng: -0.1870 },
    popularMakes: ['Toyota', 'Honda', 'Hyundai', 'Kia', 'Mercedes-Benz', 'Nissan'],
    regions: [
      {
        name: 'Greater Accra',
        slug: 'greater-accra',
        cities: [
          { name: 'Accra', slug: 'accra', adminLevel1: 'Greater Accra', hubs: ['abossey-okai', 'kokompe'] },
          { name: 'Tema', slug: 'tema', adminLevel1: 'Greater Accra', hubs: ['tema-industrial'] },
        ],
      },
      {
        name: 'Ashanti Region',
        slug: 'ashanti',
        cities: [
          { name: 'Kumasi', slug: 'kumasi', adminLevel1: 'Ashanti Region', hubs: ['kumasi-magazine'] },
        ],
      },
      {
        name: 'Western Region',
        slug: 'western',
        cities: [
          { name: 'Takoradi', slug: 'takoradi', adminLevel1: 'Western Region' },
        ],
      },
    ],
    hubs: [
      {
        slug: 'abossey-okai',
        name: 'Abossey Okai Auto Parts Market',
        countryCode: 'GH',
        region: 'Greater Accra',
        city: 'Accra',
        district: 'Ablekuma Central',
        postalCode: 'GA-142',
        geo: { lat: 5.5539, lng: -0.2312 },
        description: 'The premier and largest commercial automotive spare parts hub in Ghana and West Africa.',
        openingHours: 'Mo-Sa 07:00-18:00',
        popularCategories: ['body-parts', 'brakes', 'engine', 'suspension-steering', 'electrical-lighting'],
      },
      {
        slug: 'kumasi-magazine',
        name: 'Suame Magazine Industrial Hub',
        countryCode: 'GH',
        region: 'Ashanti Region',
        city: 'Kumasi',
        district: 'Suame',
        postalCode: 'AK-039',
        geo: { lat: 6.7198, lng: -1.6373 },
        description: 'Renowned center for automotive mechanical engineering, heavy engine rebuilds, and auto parts fabrication.',
        openingHours: 'Mo-Sa 07:30-18:00',
        popularCategories: ['engine', 'transmission', 'suspension-steering', 'heavy-machinery'],
      },
      {
        slug: 'tema-industrial',
        name: 'Tema Industrial Auto Spares',
        countryCode: 'GH',
        region: 'Greater Accra',
        city: 'Tema',
        district: 'Tema Metropolitan',
        postalCode: 'TT-012',
        geo: { lat: 5.6698, lng: -0.0166 },
        description: 'Port-proximate logistics and wholesale auto parts distribution corridor.',
        openingHours: 'Mo-Fr 08:00-17:00',
        popularCategories: ['engine', 'body-parts', 'fluids-filters'],
      },
      {
        slug: 'kokompe',
        name: 'Kokompe Industrial Hub',
        countryCode: 'GH',
        region: 'Greater Accra',
        city: 'Accra',
        district: 'Darkuman',
        postalCode: 'GA-201',
        geo: { lat: 5.5891, lng: -0.2443 },
        description: 'Specialized commercial market for heavy truck parts, transmission overhauls, and suspension components.',
        openingHours: 'Mo-Sa 08:00-18:00',
        popularCategories: ['transmission', 'suspension-steering', 'brakes'],
      },
    ],
  },
  NG: {
    code: 'NG',
    slug: 'ng',
    name: 'Nigeria',
    flag: '🇳🇬',
    demonym: 'Nigerian',
    region: 'West Africa',
    currency: 'NGN',
    currencySymbol: '₦',
    language: 'en',
    vatRate: 0.075,
    defaultReturnDays: 14,
    dialCode: '+234',
    geo: { lat: 6.5244, lng: 3.3792 },
    popularMakes: ['Toyota', 'Honda', 'Lexus', 'Mercedes-Benz', 'Ford', 'Hyundai'],
    regions: [
      {
        name: 'Lagos State',
        slug: 'lagos-state',
        cities: [
          { name: 'Lagos', slug: 'lagos', adminLevel1: 'Lagos State', hubs: ['ladipo', 'aspamda'] },
        ],
      },
      {
        name: 'Federal Capital Territory',
        slug: 'fct-abuja',
        cities: [
          { name: 'Abuja', slug: 'abuja', adminLevel1: 'FCT', hubs: ['apo-mechanics-village'] },
        ],
      },
      {
        name: 'Rivers State',
        slug: 'rivers',
        cities: [
          { name: 'Port Harcourt', slug: 'port-harcourt', adminLevel1: 'Rivers State', hubs: ['ikokwu'] },
        ],
      },
      {
        name: 'Anambra State',
        slug: 'anambra',
        cities: [
          { name: 'Nnewi', slug: 'nnewi', adminLevel1: 'Anambra State', hubs: ['nkwo-nnewi'] },
        ],
      },
      {
        name: 'Kano State',
        slug: 'kano',
        cities: [
          { name: 'Kano', slug: 'kano', adminLevel1: 'Kano State', hubs: ['kofar-ruwa'] },
        ],
      },
    ],
    hubs: [
      {
        slug: 'ladipo',
        name: 'Ladipo International Auto Spare Parts Market',
        countryCode: 'NG',
        region: 'Lagos State',
        city: 'Lagos',
        district: 'Mushin',
        postalCode: '100253',
        geo: { lat: 6.5355, lng: 3.3444 },
        description: 'West Africa’s biggest hub for genuine Japanese, German, and American replacement auto parts and half-cuts.',
        openingHours: 'Mo-Sa 08:00-18:30',
        popularCategories: ['engine', 'gearbox', 'electrical-lighting', 'body-parts', 'brakes'],
      },
      {
        slug: 'aspamda',
        name: 'ASPAMDA Trade Fair Complex',
        countryCode: 'NG',
        region: 'Lagos State',
        city: 'Lagos',
        district: 'Ojo / Badagry Expressway',
        postalCode: '102102',
        geo: { lat: 6.4673, lng: 3.2389 },
        description: 'The direct importer and primary wholesale auto parts and truck spares distribution center for Nigeria.',
        openingHours: 'Mo-Sa 08:00-17:30',
        popularCategories: ['brakes', 'suspension-steering', 'filters', 'engine-service'],
      },
      {
        slug: 'apo-mechanics-village',
        name: 'Apo Mechanics Village',
        countryCode: 'NG',
        region: 'Federal Capital Territory',
        city: 'Abuja',
        district: 'Apo District',
        postalCode: '900110',
        geo: { lat: 9.0064, lng: 7.4981 },
        description: 'Abuja’s central automotive repairs, diagnostics, and genuine replacement spare parts center.',
        openingHours: 'Mo-Sa 08:00-18:00',
        popularCategories: ['electrical-lighting', 'engine', 'brakes', 'air-conditioning'],
      },
      {
        slug: 'ikokwu',
        name: 'Port Harcourt Ikokwu Motor Spare Parts Market',
        countryCode: 'NG',
        region: 'Rivers State',
        city: 'Port Harcourt',
        district: 'Mile 2 Diobu',
        postalCode: '500272',
        geo: { lat: 4.7933, lng: 7.0019 },
        description: 'South-South Nigeria’s major commercial corridor for genuine automotive and industrial spares.',
        openingHours: 'Mo-Sa 08:00-18:00',
        popularCategories: ['engine', 'suspension-steering', 'body-parts'],
      },
      {
        slug: 'nkwo-nnewi',
        name: 'Nkwo Nnewi Auto Spares Industrial Market',
        countryCode: 'NG',
        region: 'Anambra State',
        city: 'Nnewi',
        district: 'Nkwo Triangle',
        postalCode: '435101',
        geo: { lat: 6.0194, lng: 6.9172 },
        description: 'The historic manufacturing and wholesale center of automotive parts and motorcycle components in Nigeria.',
        openingHours: 'Mo-Sa 08:00-17:30',
        popularCategories: ['motorcycle-parts', 'brakes', 'filters', 'suspension-steering'],
      },
      {
        slug: 'kofar-ruwa',
        name: 'Kofar Ruwa Auto Parts Market',
        countryCode: 'NG',
        region: 'Kano State',
        city: 'Kano',
        district: 'Kofar Ruwa',
        postalCode: '700241',
        geo: { lat: 11.9964, lng: 8.5167 },
        description: 'Northern Nigeria\'s largest commercial auto spare parts market serving Kano, Kaduna, and the greater northern corridor.',
        openingHours: 'Mo-Sa 08:00-18:00',
        popularCategories: ['engine', 'body-parts', 'brakes', 'electrical-lighting'],
      },
    ],
  },
  KE: {
    code: 'KE',
    slug: 'ke',
    name: 'Kenya',
    flag: '🇰🇪',
    demonym: 'Kenyan',
    region: 'East Africa',
    currency: 'KES',
    currencySymbol: 'KSh',
    language: 'en',
    vatRate: 0.16,
    defaultReturnDays: 7,
    dialCode: '+254',
    geo: { lat: -1.2921, lng: 36.8219 },
    popularMakes: ['Toyota', 'Subaru', 'Nissan', 'Mazda', 'Isuzu', 'Mitsubishi'],
    regions: [
      {
        name: 'Nairobi County',
        slug: 'nairobi',
        cities: [
          { name: 'Nairobi', slug: 'nairobi', adminLevel1: 'Nairobi County', hubs: ['kirinyaga', 'industrial-area'] },
        ],
      },
      {
        name: 'Mombasa County',
        slug: 'mombasa',
        cities: [
          { name: 'Mombasa', slug: 'mombasa', adminLevel1: 'Mombasa County' },
        ],
      },
    ],
    hubs: [
      {
        slug: 'kirinyaga',
        name: 'Kirinyaga Road Auto Spares Corridor',
        countryCode: 'KE',
        region: 'Nairobi County',
        city: 'Nairobi',
        district: 'Downtown CBD',
        postalCode: '00100',
        geo: { lat: -1.2821, lng: 36.8285 },
        description: 'East Africa’s primary automotive spare parts corridor in downtown Nairobi.',
        openingHours: 'Mo-Sa 08:00-18:00',
        popularCategories: ['body-parts', 'engine', 'suspension-steering', 'brakes'],
      },
      {
        slug: 'industrial-area',
        name: 'Enterprise Road Industrial Spares',
        countryCode: 'KE',
        region: 'Nairobi County',
        city: 'Nairobi',
        district: 'Enterprise Road Industrial Area',
        postalCode: '00501',
        geo: { lat: -1.3121, lng: 36.8545 },
        description: 'Heavy commercial fleet, truck parts, and industrial suspension components.',
        openingHours: 'Mo-Sa 08:00-17:30',
        popularCategories: ['heavy-commercial', 'suspension-steering', 'engine'],
      },
    ],
  },
  CI: {
    code: 'CI',
    slug: 'ci',
    name: "Côte d'Ivoire",
    flag: '🇨🇮',
    demonym: 'Ivorian',
    region: 'West Africa',
    currency: 'XOF',
    currencySymbol: 'CFA',
    language: 'fr',
    vatRate: 0.18,
    defaultReturnDays: 7,
    dialCode: '+225',
    geo: { lat: 5.3600, lng: -4.0083 },
    popularMakes: ['Toyota', 'Peugeot', 'Renault', 'Hyundai', 'Mercedes-Benz'],
    regions: [
      {
        name: 'Abidjan Autonomous District',
        slug: 'abidjan',
        cities: [
          { name: 'Abidjan', slug: 'abidjan', adminLevel1: 'Abidjan', hubs: ['marcory-pieces-auto', 'treichville-zone'] },
        ],
      },
    ],
    hubs: [
      {
        slug: 'marcory-pieces-auto',
        name: 'Marcory Pièces Détachées',
        countryCode: 'CI',
        region: 'Abidjan',
        city: 'Abidjan',
        district: 'Marcory',
        postalCode: '01',
        geo: { lat: 5.2954, lng: -3.9872 },
        description: 'The bustling hub of automotive replacement parts and accessories in Abidjan.',
        openingHours: 'Mo-Sa 07:30-18:00',
        popularCategories: ['body-parts', 'brakes', 'engine', 'fluids-filters'],
      },
      {
        slug: 'treichville-zone',
        name: 'Treichville Zone Industrielle',
        countryCode: 'CI',
        region: 'Abidjan',
        city: 'Abidjan',
        district: 'Treichville',
        postalCode: '05',
        geo: { lat: 5.3056, lng: -4.0102 },
        description: 'Heavy vehicle parts, machining workshops, and electrical supplies.',
        openingHours: 'Mo-Fr 08:00-17:30',
        popularCategories: ['electrical-lighting', 'engine', 'transmission'],
      },
    ],
  },
  ZA: {
    code: 'ZA',
    slug: 'za',
    name: 'South Africa',
    flag: '🇿🇦',
    demonym: 'South African',
    region: 'Southern Africa',
    currency: 'ZAR',
    currencySymbol: 'R',
    language: 'en',
    vatRate: 0.15,
    defaultReturnDays: 14,
    dialCode: '+27',
    geo: { lat: -26.2041, lng: 28.0473 },
    popularMakes: ['Toyota', 'Volkswagen', 'Ford', 'BMW', 'Mercedes-Benz', 'Nissan'],
    regions: [
      {
        name: 'Gauteng',
        slug: 'gauteng',
        cities: [
          { name: 'Johannesburg', slug: 'johannesburg', adminLevel1: 'Gauteng', hubs: ['midrand-spares'] },
        ],
      },
      {
        name: 'Western Cape',
        slug: 'western-cape',
        cities: [
          { name: 'Cape Town', slug: 'cape-town', adminLevel1: 'Western Cape', hubs: ['stikland-industrial'] },
        ],
      },
    ],
    hubs: [
      {
        slug: 'midrand-spares',
        name: 'Midrand Motor Spares Hub',
        countryCode: 'ZA',
        region: 'Gauteng',
        city: 'Johannesburg',
        district: 'Midrand Industrial Park',
        postalCode: '1685',
        geo: { lat: -25.9992, lng: 28.1263 },
        description: 'Gauteng’s strategic automotive parts and OEM components distribution logistics node.',
        openingHours: 'Mo-Fr 07:30-17:30, Sa 08:00-13:00',
        popularCategories: ['brakes', 'engine', 'suspension-steering', 'body-parts'],
      },
      {
        slug: 'stikland-industrial',
        name: 'Stikland Industrial Spares',
        countryCode: 'ZA',
        region: 'Western Cape',
        city: 'Cape Town',
        district: 'Bellville',
        postalCode: '7530',
        geo: { lat: -33.9067, lng: 18.6653 },
        description: 'Cape Town automotive repair and commercial replacement parts sector.',
        openingHours: 'Mo-Fr 08:00-17:00',
        popularCategories: ['engine', 'transmission', 'brakes'],
      },
    ],
  },
  SN: {
    code: 'SN',
    slug: 'sn',
    name: 'Senegal',
    flag: '🇸🇳',
    demonym: 'Senegalese',
    region: 'West Africa',
    currency: 'XOF',
    currencySymbol: 'CFA',
    language: 'fr',
    vatRate: 0.18,
    defaultReturnDays: 7,
    dialCode: '+221',
    geo: { lat: 14.7167, lng: -17.4677 },
    popularMakes: ['Toyota', 'Peugeot', 'Renault', 'Mitsubishi', 'Hyundai'],
    regions: [
      {
        name: 'Dakar Region',
        slug: 'dakar',
        cities: [
          { name: 'Dakar', slug: 'dakar', adminLevel1: 'Dakar', hubs: ['colobane'] },
        ],
      },
    ],
    hubs: [
      {
        slug: 'colobane',
        name: 'Colobane Auto Spares Market',
        countryCode: 'SN',
        region: 'Dakar',
        city: 'Dakar',
        district: 'Colobane',
        postalCode: '11000',
        geo: { lat: 14.6922, lng: -17.4478 },
        description: 'The historic and most active spare parts market for French and Japanese autos in Dakar.',
        openingHours: 'Mo-Sa 08:00-18:00',
        popularCategories: ['body-parts', 'engine', 'electrical-lighting'],
      },
    ],
  },
  TG: {
    code: 'TG',
    slug: 'tg',
    name: 'Togo',
    flag: '🇹🇬',
    demonym: 'Togolese',
    region: 'West Africa',
    currency: 'XOF',
    currencySymbol: 'CFA',
    language: 'fr',
    vatRate: 0.18,
    defaultReturnDays: 7,
    dialCode: '+228',
    geo: { lat: 6.1375, lng: 1.2125 },
    popularMakes: ['Toyota', 'Honda', 'Nissan', 'Peugeot'],
    regions: [
      {
        name: 'Maritime Region',
        slug: 'maritime',
        cities: [
          { name: 'Lomé', slug: 'lome', adminLevel1: 'Maritime', hubs: ['lome-grand-marche'] },
        ],
      },
    ],
    hubs: [
      {
        slug: 'lome-grand-marche',
        name: 'Grand Marché Auto Spares Hub',
        countryCode: 'TG',
        region: 'Maritime',
        city: 'Lomé',
        district: 'Lomé Centre',
        postalCode: '0000',
        geo: { lat: 6.1287, lng: 1.2215 },
        description: 'Lomé’s central transit and import point for cross-border auto components between Ghana and Benin.',
        openingHours: 'Mo-Sa 08:00-17:30',
        popularCategories: ['engine', 'body-parts', 'suspension-steering'],
      },
    ],
  },
  BJ: {
    code: 'BJ',
    slug: 'bj',
    name: 'Benin',
    flag: '🇧🇯',
    demonym: 'Beninese',
    region: 'West Africa',
    currency: 'XOF',
    currencySymbol: 'CFA',
    language: 'fr',
    vatRate: 0.18,
    defaultReturnDays: 7,
    dialCode: '+229',
    geo: { lat: 6.3703, lng: 2.3912 },
    popularMakes: ['Toyota', 'Honda', 'Peugeot', 'Nissan'],
    regions: [
      {
        name: 'Littoral Department',
        slug: 'littoral',
        cities: [
          { name: 'Cotonou', slug: 'cotonou', adminLevel1: 'Littoral', hubs: ['dantokpa-auto'] },
        ],
      },
    ],
    hubs: [
      {
        slug: 'dantokpa-auto',
        name: 'Dantokpa Pièces Auto',
        countryCode: 'BJ',
        region: 'Littoral',
        city: 'Cotonou',
        district: 'Dantokpa',
        postalCode: '0000',
        geo: { lat: 6.3776, lng: 2.4344 },
        description: 'One of the largest open-air markets in West Africa with a major automotive spares zone.',
        openingHours: 'Mo-Sa 07:30-18:00',
        popularCategories: ['brakes', 'engine', 'body-parts'],
      },
    ],
  },
};

/**
 * Normalizes input country code or slug into valid CountryNode
 */
export function getCountry(countryCodeOrSlug?: string): CountryNode {
  if (!countryCodeOrSlug) return AFRICAN_GEOGRAPH.NG;
  const upper = String(countryCodeOrSlug).trim().toUpperCase();
  if (AFRICAN_GEOGRAPH[upper]) {
    return AFRICAN_GEOGRAPH[upper];
  }
  // Try matching by lowercase slug
  const lower = String(countryCodeOrSlug).trim().toLowerCase();
  const found = Object.values(AFRICAN_GEOGRAPH).find((c) => c.slug === lower);
  return found || AFRICAN_GEOGRAPH.NG;
}

export const SUPPORTED_MARKET_CODES = ['NG', 'GH', 'KE', 'ZA', 'CI', 'SN', 'TG', 'BJ'] as const;
export const SUPPORTED_MARKET_SLUGS = ['ng', 'gh', 'ke', 'za', 'ci', 'sn', 'tg', 'bj'] as const;

/**
 * Returns all active country nodes in priority order: ng, gh, ke, za, ci, sn, tg, bj
 */
export function getAllCountries(): CountryNode[] {
  return SUPPORTED_MARKET_CODES.map((code) => AFRICAN_GEOGRAPH[code]).filter(Boolean);
}

/**
 * Returns list of 2-letter lowercase country slugs for routing in priority order
 */
export function getAllCountrySlugs(): string[] {
  return [...SUPPORTED_MARKET_SLUGS];
}

export const HUB_SLUG_ALIASES: Record<string, string> = {
  // Nigeria
  'ladipo-market': 'ladipo',
  'ladipo-international': 'ladipo',
  'ladipo-central': 'ladipo',
  'trade-fair': 'aspamda',
  'aspamda-trade-fair': 'aspamda',
  'apo-mechanic': 'apo-mechanics-village',
  'apo-village': 'apo-mechanics-village',
  'ikokwu-market': 'ikokwu',
  'port-harcourt-ikokwu': 'ikokwu',
  'nnewi': 'nkwo-nnewi',
  'nnewi-parts': 'nkwo-nnewi',
  'kano-kofar': 'kofar-ruwa',
  'kofar-ruwa-kano': 'kofar-ruwa',
  'kano-spare-parts': 'kofar-ruwa',
  // Ghana
  'abossey-okai-market': 'abossey-okai',
  'suame-magazine': 'kumasi-magazine',
  'suame': 'kumasi-magazine',
  'kumasi': 'kumasi-magazine',
  'tema': 'tema-industrial',
  // Kenya
  'kirinyaga-road': 'kirinyaga',
  'enterprise-road': 'industrial-area',
  // Côte d'Ivoire
  'treichville': 'treichville-zone',
  'marcory': 'marcory-pieces-auto',
  // South Africa
  'johannesburg-central': 'midrand-spares',
  'midrand': 'midrand-spares',
  'durban-south': 'stikland-industrial',
  'stikland': 'stikland-industrial',
};

/**
 * Returns all commercial market hubs for a given country
 */
export function getCountryHubs(countryCodeOrSlug?: string): MarketHubNode[] {
  const country = getCountry(countryCodeOrSlug);
  return country.hubs || [];
}

/**
 * Finds a market hub by its slug across all countries, resolving aliases
 */
export function getHubBySlug(slug: string): MarketHubNode | undefined {
  const clean = String(slug).toLowerCase().trim();
  const canonical = HUB_SLUG_ALIASES[clean] || clean;
  for (const country of Object.values(AFRICAN_GEOGRAPH)) {
    const hub = country.hubs.find((h) => h.slug === canonical || h.slug === clean);
    if (hub) return hub;
  }
  return undefined;
}

/**
 * Returns the authentic country flag emoji for a given country code or slug
 */
export function getCountryFlag(codeOrSlug?: string): string {
  if (!codeOrSlug) return '🇳🇬';
  const clean = String(codeOrSlug).trim().toUpperCase();
  const flagMap: Record<string, string> = {
    GH: '🇬🇭',
    NG: '🇳🇬',
    KE: '🇰🇪',
    CI: '🇨🇮',
    ZA: '🇿🇦',
    SN: '🇸🇳',
    TG: '🇹🇬',
    BJ: '🇧🇯',
  };
  if (flagMap[clean]) return flagMap[clean];
  const country = getCountry(codeOrSlug);
  return country?.flag || flagMap[country?.code] || '🌐';
}


